/**
 * Web equivalent of the mobile TransactionReviewModal.
 *
 * Step 3 of the Buying/Listing CREATE-LAST (and convert-to-active) flow. Shows
 * the AI-parsed timeline so the agent can VERIFY and EDIT it (dates + action
 * owners) before committing. No backend call is made here — on "Reviewed" the
 * edited timeline is handed back to the parent via onReviewed(editedTimeline),
 * which persists it when the transaction is finally created/converted.
 */

import React, { useMemo, useState, useEffect } from 'react';
import './TransactionReviewModal.css';

// Milestone types that get a "Contingency" badge — matches mobile CONTINGENCY_TYPES set
const CONTINGENCY_TYPES = new Set([
  'LOAN_CONTINGENCY',
  'APPRAISAL_CONTINGENCY',
  'INVESTIGATION_CONTINGENCY',
  'TITLE_CONTINGENCY',
  'INSURANCE_CONTINGENCY',
  'SALE_OF_BUYER_PROPERTY_CONTINGENCY',
  'CONTINGENCY_REMOVAL',
  'HOA_DOCUMENTS_REVIEW',
]);

// Negotiation-only types that live on the parser output but are stripped
// from the active timeline on the backend (MilestoneTypeFilter.NEGOTIATION_ONLY_TYPES).
// Hide them in the review screen too so the agent isn't shown rows that
// won't appear post-convert.
const NEGOTIATION_ONLY_TYPES = new Set([
  'OFFER_RECEIVED',
  'OFFER_EXPIRATION',
  'COUNTER_OFFER_EXPIRATION',
]);

// Action-owner roles (agents first, then principals) for the editable owner chips.
const OWNER_ROLES = ['BUYER_AGENT', 'SELLER_AGENT', 'BUYER', 'SELLER'];
const OWNER_ROLE_LABELS = { BUYER_AGENT: "Buyer's Agent", SELLER_AGENT: "Seller's Agent", BUYER: 'Buyer', SELLER: 'Seller' };
// Roles the parser marked ACTIONABLE on a milestone = its current action owners.
const ownersFromPermissions = (permissions) => {
  if (!permissions || typeof permissions !== 'object') return [];
  return OWNER_ROLES.filter((r) => String(permissions[r]).toUpperCase() === 'ACTIONABLE');
};
// Inverse — rebuild a permissions map from a chosen owner list (for persistence).
const permissionsFromOwners = (owners) =>
  Object.fromEntries(OWNER_ROLES.map((r) => [r, owners.includes(r) ? 'ACTIONABLE' : 'READ_ONLY']));

// Normalize a parser date ("mm/dd/yyyy" or ISO) to the YYYY-MM-DD an <input type=date> needs.
const dateToInput = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  const parts = String(dateStr).split('/');
  if (parts.length === 3) {
    const [m, d, y] = parts;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
};

// Pretty-print a YYYY-MM-DD for read-only display, using local date components.
const prettyDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? iso : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const TransactionReviewModal = ({ visible, parsed, side, onReviewed, onCancel }) => {
  // Only own-side roles are selectable action owners — the opposite side is always
  // read-only, so it never appears in the dropdown. BUYING -> Buyer's Agent + Buyer;
  // LISTING -> Seller's Agent + Seller. When a milestone resolves to no owner, the
  // default is the own-side agent.
  const sideUp = String(side || '').toUpperCase();
  const isListing = sideUp === 'LISTING' || sideUp === 'SALE' || sideUp === 'SELLING';
  const ownerRolesForSide = isListing ? ['SELLER_AGENT', 'SELLER'] : ['BUYER_AGENT', 'BUYER'];
  const defaultAgentRole = isListing ? 'SELLER_AGENT' : 'BUYER_AGENT';

  // Review-screen edits (pre-create): keyed by the milestone's index in the ORIGINAL
  // parsed.timeline (stable + unique even when two milestones share a type).
  const [edits, setEdits] = useState({});          // index -> { dueDate?, owners? }
  const [editingKeys, setEditingKeys] = useState({}); // index -> true while that row is in edit mode

  // Reset edits whenever a new parse is shown.
  useEffect(() => { setEdits({}); setEditingKeys({}); }, [parsed]);

  // Displayed rows: carry each milestone's original index so edits map back to the full list.
  const rows = useMemo(() => {
    const withIdx = (parsed?.timeline ?? []).map((m, originalIndex) => ({ m, originalIndex }));
    return withIdx
      .filter(({ m }) => m?.type && !NEGOTIATION_ONLY_TYPES.has(m.type))
      .sort((a, b) => (a.m?.sortOrder ?? 0) - (b.m?.sortOrder ?? 0));
  }, [parsed]);

  if (!visible) return null;

  const ownersFor = (m, idx) => {
    if (edits[idx]?.owners !== undefined) return edits[idx].owners;
    // Keep only own-side roles the parser marked ACTIONABLE; fall back to the
    // own-side agent so every milestone always has a concrete default owner.
    const fromPerms = ownersFromPermissions(m.permissions).filter((r) => ownerRolesForSide.includes(r));
    return fromPerms.length > 0 ? fromPerms : [defaultAgentRole];
  };
  const dateFor = (m, idx) => (edits[idx]?.dueDate !== undefined ? edits[idx].dueDate : dateToInput(m?.deadline ?? m?.dueDate));

  const setDate = (idx, value) => setEdits((prev) => ({ ...prev, [idx]: { ...prev[idx], dueDate: value } }));
  // One actionable owner; everyone else is read-only by default.
  const setOwner = (idx, role) => setEdits((prev) => ({ ...prev, [idx]: { ...prev[idx], owners: role ? [role] : [] } }));
  const toggleEditing = (idx) => setEditingKeys((prev) => ({ ...prev, [idx]: !prev[idx] }));

  // Apply the edits back onto the FULL parsed timeline and hand it to the parent.
  const handleReviewed = () => {
    const editedTimeline = (parsed?.timeline ?? []).map((m, idx) => {
      const e = edits[idx];
      if (!e) return m;
      return {
        ...m,
        ...(e.dueDate !== undefined ? { dueDate: e.dueDate } : {}),
        ...(e.owners !== undefined ? { permissions: permissionsFromOwners(e.owners) } : {}),
      };
    });
    onReviewed?.(editedTimeline);
  };

  return (
    <div className="trv-overlay" onClick={onCancel}>
      <div className="trv-container" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="trv-header">
          <span className="trv-title">Review timeline</span>
          <button className="trv-close-btn" onClick={onCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="trv-body">
          <div className="trv-section-header">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2D1B69" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span className="trv-section-title">Review &amp; edit your transaction timeline</span>
          </div>

          {rows.length === 0 ? (
            <p className="trv-empty">No milestones returned.</p>
          ) : (
            <div className="trv-timeline">
              {rows.map(({ m, originalIndex }, i) => {
                const title = m?.title ?? m?.name ?? '—';
                const isContingency = !!m?.type && CONTINGENCY_TYPES.has(m.type);
                const isLast = i === rows.length - 1;
                const statusText = m?.status ?? 'PENDING';
                const owners = ownersFor(m, originalIndex);
                const dateVal = dateFor(m, originalIndex);
                const isEditing = !!editingKeys[originalIndex];

                return (
                  <div key={originalIndex} className={`trv-milestone-row${isEditing ? ' trv-milestone-row-editing' : ''}`}>
                    <div className="trv-icon-col">
                      <div className="trv-milestone-circle" />
                      {!isLast && <div className="trv-connecting-line" />}
                    </div>
                    <div className="trv-milestone-content">
                      <div className="trv-milestone-title-row">
                        <span className="trv-milestone-name">{title}</span>
                        {isContingency && (
                          <span className="trv-contingency-badge">Contingency</span>
                        )}
                      </div>
                      <span className="trv-milestone-status">{statusText}</span>
                      {isEditing ? (
                        <select
                          className="trv-owner-select"
                          value={owners[0] || defaultAgentRole}
                          onChange={(e) => setOwner(originalIndex, e.target.value)}
                        >
                          {ownerRolesForSide.map((role) => (
                            <option key={role} value={role}>{OWNER_ROLE_LABELS[role]}</option>
                          ))}
                        </select>
                      ) : (
                        owners.length > 0 && (
                          <div className="trv-milestone-owners">
                            {owners.map((r) => (
                              <span key={r} className="trv-owner-chip-static">{OWNER_ROLE_LABELS[r]}</span>
                            ))}
                          </div>
                        )
                      )}
                    </div>
                    <div className="trv-milestone-right">
                      {isEditing ? (
                        <input
                          type="date"
                          className="trv-date-input"
                          value={dateVal || ''}
                          onChange={(e) => setDate(originalIndex, e.target.value)}
                        />
                      ) : dateVal ? (
                        <div className="trv-milestone-due">{prettyDate(dateVal)}</div>
                      ) : m?.anchor === 'ACCEPTANCE' ? (
                        <div className="trv-milestone-due trv-milestone-due-pending">Date pending</div>
                      ) : null}
                      <button
                        type="button"
                        className="trv-edit-icon-btn"
                        title={isEditing ? 'Done editing' : 'Edit date & action owners'}
                        onClick={() => toggleEditing(originalIndex)}
                      >
                        {isEditing ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="trv-footer">
          <button className="trv-btn trv-btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="trv-btn trv-btn-primary" onClick={handleReviewed}>Reviewed</button>
        </div>

      </div>
    </div>
  );
};

export default TransactionReviewModal;
